import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPrice, formatArea, getLabel, PROPERTY_TYPES } from '../utils/propertyConstants';
import { useWishlist } from '../contexts/WishlistContext';
import { useToast } from '../contexts/ToastContext';

const PropertyCard = ({ property, onWishlistUpdate }) => {
  const navigate = useNavigate();
  const { isInWishlist, toggleWishlist, isAuthenticated } = useWishlist();
  const { showSuccess, showError } = useToast();
  const propertyId = property._id || property.id;
  const isFavorite = isInWishlist(propertyId);

  const handleClick = (e) => {
    if (e.target.closest('button') || e.target.closest('.favorite-btn')) {
      return;
    }
    if (propertyId) {
      navigate(`/property/${propertyId}`);
    }
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    // Check auth at click time - context's isAuthenticated can be stale after login
    if (!sessionStorage.getItem('authToken')) {
      showError('Please login to add properties to your wishlist');
      navigate('/login');
      return;
    }
    const result = await toggleWishlist(propertyId);
    if (result.success) {
      showSuccess(result.action === 'added' ? 'Added to wishlist' : 'Removed from wishlist');
      onWishlistUpdate?.();
    } else if (result.needsLogin) {
      showError('Please login to add to wishlist');
      navigate('/login');
    } else if (result.error) {
      showError(result.error);
    }
  };

  // Get first image or placeholder
  const imageUrl = property.media?.images?.[0]?.url || 'https://via.placeholder.com/400x300?text=No+Image';
  const propertyTypeLabel = getLabel(PROPERTY_TYPES, property.propertyType);

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-xl shadow-sm hover:shadow-md cursor-pointer overflow-hidden transition-shadow duration-200"
    >
      {/* Image */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={imageUrl}
          alt={property.title}
          className="w-full h-full object-cover"
        />
        {/* Verified Badge */}
        <div className="absolute top-3 left-3 bg-white px-2.5 py-1 rounded-full text-xs font-medium text-gray-800">
          Verified
        </div>
        {/* Favorite Icon */}
        <button
          onClick={handleFavoriteClick}
          className="absolute top-3 right-3 favorite-btn z-10"
        >
          <svg
            className={`w-6 h-6 ${isFavorite ? 'fill-red-500' : 'fill-white stroke-gray-400'}`}
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth="2"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>
      </div>

      {/* Content - Minimal */}
      <div className="p-4">
        {/* Price */}
        <div className="mb-2">
          <span className="text-2xl font-bold text-gray-900">
            {formatPrice(property.price)}
          </span>
          <span className="text-gray-600 text-sm ml-1">
            /{property.priceType === 'per_month' ? 'month' : 'night'}
          </span>
        </div>

        {/* Property Type and Location */}
        <div className="text-gray-700 text-sm mb-2">
          {property.bhk}BHK {propertyTypeLabel} in {property.locality}
        </div>

        {/* Key Specifications */}
        <div className="text-gray-600 text-xs flex items-center gap-1.5">
          <span>{formatArea(property.area, property.areaUnit)}</span>
          <span>•</span>
          <span>{property.bhk} Beds</span>
          <span>•</span>
          <span>{property.bathrooms} Baths</span>
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;

